import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grid, TextField, Typography } from '@mui/material';

import AlgorithmsField from './AlgorithmsField';
import RequiredClaimsField from './RequiredClaimsField';

function buildOidcPayload({ issuer, audience, algorithms, jwks_uri, required_claims }) {
  const next = {
    issuer,
    audience,
    algorithms,
  };
  if (jwks_uri) {
    next.jwks_uri = jwks_uri;
  }
  if (required_claims) {
    next.required_claims = required_claims;
  }
  return next;
}

function hasOidcError({ issuer, audience, algorithms }) {
  return !issuer || !audience || !algorithms?.length;
}

export default function OidcFields({
  value,
  onChange,
  disabled = false,
  stacked = false,
}) {
  const [issuer, setIssuer] = useState(value?.issuer || '');
  const [audience, setAudience] = useState(value?.audience || '');
  const [jwks_uri, setJwksUri] = useState(value?.jwks_uri || '');
  const [algorithms, setAlgorithms] = useState(
    Array.isArray(value?.algorithms) ? value.algorithms : [],
  );
  const [required_claims, setRequiredClaims] = useState(
    value?.required_claims,
  );

  const onChangeRef = useRef(onChange);
  const isInitialMount = useRef(true);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const notifyParent = useCallback((state) => {
    const payload = buildOidcPayload(state);
    onChangeRef.current(payload, hasOidcError(state));
  }, []);

  const updateOidc = useCallback(
    (patch) => {
      const next_state = {
        issuer: patch.issuer !== undefined ? patch.issuer : issuer,
        audience: patch.audience !== undefined ? patch.audience : audience,
        jwks_uri: patch.jwks_uri !== undefined ? patch.jwks_uri : jwks_uri,
        algorithms:
          patch.algorithms !== undefined ? patch.algorithms : algorithms,
        required_claims:
          patch.required_claims !== undefined
            ? patch.required_claims
            : required_claims,
      };

      if (patch.issuer !== undefined) {
        setIssuer(patch.issuer);
      }
      if (patch.audience !== undefined) {
        setAudience(patch.audience);
      }
      if (patch.jwks_uri !== undefined) {
        setJwksUri(patch.jwks_uri);
      }
      if (patch.algorithms !== undefined) {
        setAlgorithms(patch.algorithms);
      }
      if (patch.required_claims !== undefined) {
        setRequiredClaims(patch.required_claims);
      }

      notifyParent(next_state);
    },
    [issuer, audience, jwks_uri, algorithms, required_claims, notifyParent],
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      notifyParent({
        issuer,
        audience,
        jwks_uri,
        algorithms,
        required_claims,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Typography variant='body1' fontWeight='medium'>
          OIDC Settings
        </Typography>
      </Grid>

      <Grid size={stacked ? 12 : { xs: 12, md: 6 }}>
        <TextField
          label='Issuer'
          value={issuer}
          onChange={(e) => updateOidc({ issuer: e.target.value })}
          fullWidth
          disabled={disabled}
          required
          error={!issuer}
        />
      </Grid>

      <Grid size={stacked ? 12 : { xs: 12, md: 6 }}>
        <TextField
          label='Audience'
          value={audience}
          onChange={(e) => updateOidc({ audience: e.target.value })}
          fullWidth
          disabled={disabled}
          required
          error={!audience}
        />
      </Grid>

      <Grid size={stacked ? 12 : { xs: 12, md: 6 }}>
        <AlgorithmsField
          value={algorithms}
          onChange={(next_algorithms) =>
            updateOidc({ algorithms: next_algorithms })
          }
          disabled={disabled}
          required
          error={algorithms.length === 0}
        />
      </Grid>

      <Grid size={stacked ? 12 : { xs: 12, md: 6 }}>
        <TextField
          label='JWKS URI (recommended)'
          value={jwks_uri}
          onChange={(e) => updateOidc({ jwks_uri: e.target.value })}
          fullWidth
          disabled={disabled}
        />
      </Grid>

      <Grid size={12}>
        <RequiredClaimsField
          value={required_claims}
          onChange={(next_claims) =>
            updateOidc({ required_claims: next_claims })
          }
          disabled={disabled}
          stacked={stacked}
        />
      </Grid>

      {hasOidcError({ issuer, audience, algorithms }) ? (
        <Grid size={12}>
          <Typography variant='body2' color='error'>
            Issuer, Audience, and at least one Algorithm are required.
          </Typography>
        </Grid>
      ) : null}
    </Grid>
  );
}
